import { useReactiveVar } from '@bestseller/retail-planning.utils.reactive-vars';
import { History, Loop, PriorityHigh } from '@mui/icons-material';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ColDef, IDetailCellRendererParams } from 'ag-grid-community';
import { format } from 'date-fns';
import type { JSX } from 'react';
import { useMemo } from 'react';
import { ComparableCell } from 'src/components/molecules/ComparableCell/ComparableCell';
import { IconCell } from 'src/components/molecules/IconCell/IconCell';
import {
    calculateMeanMonthlyAverages,
    calculateWeightedAverages,
} from 'src/components/organisms/DataTable/aggregators/average-total.agg';
import { DefaultColumn } from 'src/components/organisms/DataTable/columns/default-columns-details';
import { ScopeEnum } from 'src/domain';
import { useIsUsingBuyingBudget } from 'src/hooks/chain-settings/useIsUsingBuyingBudget';
import { useDeliveryProfileOverviewSimulationRows } from 'src/hooks/delivery-profile/simulation/useDeliveryProfileOverviewSimulationRows';
import { useMarkupOverviewSimulationRows } from 'src/hooks/markup/simulation/useMarkupOverviewSimulationRows';
import { useScope } from 'src/hooks/useScope';
import { useStoreType } from 'src/hooks/useStoreType';
import { comparableStoreTypeVar } from 'src/infrastructure/local_state';
import { FccSalesBudgetModel } from 'src/infrastructure/rest-api/api-types';
import { ExpectedSalesDataComparable, mapTurnoverToTableData } from 'src/mapping/turnover-mapping';
import { getPreviousMonthKey, groupDataByMonthKey } from 'src/utils/monthKeys';
import { numberWithSpaces } from 'src/utils/numberWithSpaces';
import { calculateCogs, calculateDSPForMonthKey } from 'src/utils/turnover/calculateCogs';
import { useTurnoverQuery } from '../queries/useTurnoverQuery';

export const useTurnoverRows = () => {
    const storeType = useStoreType();
    const showBuyingBudget = useIsUsingBuyingBudget();
    const { expectedSalesComparable, loading, error } = useTurnoverQuery() ?? {};
    const [comparableStoreType] = useReactiveVar(comparableStoreTypeVar);
    const tableData = useMemo(
        () => mapTurnoverToTableData(expectedSalesComparable as ExpectedSalesDataComparable, comparableStoreType),
        [expectedSalesComparable, comparableStoreType]
    );

    const { data: markupData } = useMarkupOverviewSimulationRows() ?? {};
    const { data: deliveryData } = useDeliveryProfileOverviewSimulationRows();
    const extendedTableData = useMemo(() => {
        if (!markupData || !deliveryData || !showBuyingBudget) return tableData;

        const markupAvg = calculateWeightedAverages(markupData);
        const deliveryProfileAvg = calculateMeanMonthlyAverages(deliveryData);

        const tableDataByMonthKey = groupDataByMonthKey(tableData);

        const res = tableData.map((row) => {
            const currentMonthKey = row.monthKey;
            const previousMonthKey = getPreviousMonthKey(currentMonthKey);

            const dspCurrentMonth = calculateDSPForMonthKey(
                currentMonthKey,
                tableDataByMonthKey,
                markupAvg,
                deliveryProfileAvg
            );

            const dspPreviousMonth = calculateDSPForMonthKey(
                previousMonthKey,
                tableDataByMonthKey,
                markupAvg,
                deliveryProfileAvg
            );

            const plannedSalesIv = row.plannedSalesIv ?? 0;
            const expectedCogs = calculateCogs(plannedSalesIv, markupAvg[currentMonthKey]) ?? 0;
            const dspChange = dspCurrentMonth - dspPreviousMonth;
            const buyingBudget = expectedCogs + dspChange;

            return {
                ...row,
                buyingBudget,
            };
        });

        return res;
    }, [tableData, markupData, deliveryData, showBuyingBudget]);

    const scope = useScope();

    const columns = useMemo(
        (): ColDef[] => [
            {
                ...DefaultColumn,
                field: 'month',
                width: 175,
                headerName: 'Month',
            },
            {
                ...DefaultColumn,
                type: 'numericColumn',
                field: 'fccInitial',
                width: 125,
                headerName: 'FCC Initial iV',
                headerTooltip: 'FCC Initial including VAT',
                hide: storeType !== 'OWN_STORE',
                cellRenderer: IconCell,
                cellRendererParams: (params: IDetailCellRendererParams) => {
                    const historicalInitBudgets = params.data.historicalFccBudgets.filter(
                        (budget: FccSalesBudgetModel) => budget.budgetSource === 'BudgetInit'
                    );
                    return {
                        showIcon:
                            scope === ScopeEnum.STORE
                                ? shouldShowIcon(params?.data?.fccInitialBudgetYear, params?.data?.fccInitialReused)
                                : false,
                        tooltip: historicalInitBudgets.length
                            ? getHistoricalFccBudgetsTooltip(historicalInitBudgets)
                            : getToolTip(params?.data?.fccInitialBudgetYear),
                        icon: historicalInitBudgets.length ? (
                            <HistoryIcon />
                        ) : (
                            getIcon(params?.data?.fccInitialBudgetYear)
                        ),
                    };
                },
            },
            {
                ...DefaultColumn,
                type: 'numericColumn',
                field: 'fccRevised',
                width: 135,
                headerName: 'FCC Revised iV',
                headerTooltip: 'FCC Revised including VAT',
                hide: storeType !== 'OWN_STORE',
                cellRenderer: IconCell,
                cellRendererParams: (params: IDetailCellRendererParams) => {
                    const historicalRevBudgets = params.data.historicalFccBudgets.filter(
                        (budget: FccSalesBudgetModel) => budget.budgetSource === 'BudgetRev'
                    );
                    return {
                        showIcon:
                            scope === ScopeEnum.STORE
                                ? shouldShowIcon(params?.data?.fccRevisedBudgetYear, params?.data?.fccRevisedReused)
                                : false,
                        tooltip: historicalRevBudgets.length
                            ? getHistoricalFccBudgetsTooltip(historicalRevBudgets)
                            : getToolTip(params?.data?.fccRevisedBudgetYear),
                        icon: historicalRevBudgets.length ? (
                            <HistoryIcon />
                        ) : (
                            getIcon(params?.data?.fccRevisedBudgetYear)
                        ),
                    };
                },
            },
            {
                ...DefaultColumn,
                type: 'numericColumn',
                field: 'salesIvLly',
                width: 140,
                headerName: 'Sales iV LLY',
                headerTooltip: 'Sales iV LLY',
            },
            {
                ...DefaultColumn,
                type: 'numericColumn',
                field: 'salesIvLy',
                width: 140,
                headerName: 'Sales iV LY',
                headerTooltip: 'Sales Iv LY',
                valueGetter: (params) => {
                    return (params.node?.rowIndex ?? 0) < 12 ? params.data.salesIvLy : undefined;
                },
            },
            {
                ...DefaultColumn,
                type: 'numericColumn',
                field: 'indexLy',
                width: 110,
                headerName: 'Index LY',
                headerTooltip: 'Index LY',
                valueGetter: (params) => {
                    return (params.node?.rowIndex ?? 0) < 12 ? params.data.indexLy : undefined;
                },
            },
            {
                ...DefaultColumn,
                type: 'numericColumn',
                field: 'plannedSalesIv',
                width: 180,
                headerName: 'Planned Sales iV',
                headerTooltip: 'Planned Sales iV',
                cellRenderer: ComparableCell,
            },
            {
                ...DefaultColumn,
                type: 'numericColumn',
                field: 'plannedIndex',
                width: 155,
                headerName: 'Planned Index',
                headerTooltip: 'Planned Index to LY',
                cellRenderer: ComparableCell,
            },
            {
                ...DefaultColumn,
                hide: !showBuyingBudget,
                type: 'numericColumn',
                field: 'buyingBudget',
                width: 155,
                headerName: 'Buying Budget',
                headerTooltip: 'Buying Budget',
            },
        ],
        [scope, storeType, showBuyingBudget]
    );

    return { data: extendedTableData, columns, error, loading };
};

const ReusedIcon = styled(Loop)`
  font-size: 14px;
`;

const AlertIcon = styled(PriorityHigh)`
  font-size: 14px;
`;
const HistoryIcon = styled(History)`
  font-size: 14px;
`;

const getIcon = (year: number): JSX.Element => {
    if (!year) return <AlertIcon />;
    return <ReusedIcon />;
};

const getToolTip = (year: number): string => {
    if (!year) return 'No historical value found';
    return `Reused value: ${year}`;
};

const getHistoricalFccBudgetsTooltip = (budgets: FccSalesBudgetModel[]) => {
    return (
        <Box>
            <TooltipRow>
                <TooltipHeader sx={{ flexGrow: 1 }}>Date</TooltipHeader>
                <TooltipHeader>Amount</TooltipHeader>
            </TooltipRow>
            {budgets.map((budget, key: number) => (
                <TooltipRow key={key}>
                    <Box>{format(new Date(budget.budgetStartDate), 'dd-MM-y')}</Box>
                    <Box>{numberWithSpaces(Math.round(budget.amountLcy as number))}</Box>
                </TooltipRow>
            ))}
        </Box>
    );
};

const shouldShowIcon = (year: number, reused: boolean): boolean => {
    return reused || !year;
};

const TooltipRow = styled(Box)`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const TooltipHeader = styled(Box)`
  font-weight: bold;
`;
